/**
 * ViewVersionDialog — read-only viewer for any prompt version.
 *
 * Fetches `GET /admin/prompts/:id/versions/:versionId` (which returns the full
 * `PromptVersion` shape with systemPrompt + fewShotExamples + jsonOutputSchema)
 * and renders it via PromptEditor in `disabled` mode.
 *
 * Closes the read gap exposed in operations 2026-04-29: previously the only
 * way to inspect a prompt's content was via DB. Now operators can read the
 * active prompt before drafting v2 — and the "Editar como nova versão" CTA
 * forks the content into NewVersionDialog so they don't start from scratch.
 */

import { useQuery } from '@tanstack/react-query';
import * as React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiClient } from '@/lib/api';
import type { PromptVersion } from '@/lib/prompts-types';
import { PromptEditor, type PromptEditorValue } from './PromptEditor';

export interface ViewVersionDialogProps {
  promptId: string;
  versionId: string;
  versionNumber: number;
  isActive: boolean;
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /**
   * Called when the operator clicks "Editar como nova versão". Receives the
   * editor-shaped value so the parent can hand it off to NewVersionDialog.
   */
  onForkAsNewVersion?: (seed: PromptEditorValue) => void;
}

function toEditorValue(version: PromptVersion): PromptEditorValue {
  return {
    systemPrompt: version.systemPrompt,
    fewShotsText: JSON.stringify(version.fewShotExamples ?? [], null, 2),
    jsonSchemaText: JSON.stringify(version.jsonOutputSchema ?? {}, null, 2),
    changeSummary: '',
  };
}

export function ViewVersionDialog({
  promptId,
  versionId,
  versionNumber,
  isActive,
  open,
  onOpenChange,
  onForkAsNewVersion,
}: ViewVersionDialogProps) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'prompts', promptId, 'versions', versionId, 'full'],
    queryFn: () => apiClient.get<PromptVersion>(`/admin/prompts/${promptId}/versions/${versionId}`),
    enabled: open,
  });

  const editorValue = React.useMemo(
    () =>
      data
        ? toEditorValue(data)
        : { systemPrompt: '', fewShotsText: '[]', jsonSchemaText: '{}', changeSummary: '' },
    [data],
  );

  const noop = React.useCallback(() => {
    // intentionally empty — read-only mode
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-4 overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            Versão v{versionNumber}
            {isActive ? ' (ativa)' : ''}
          </DialogTitle>
          <DialogDescription>
            Visualização somente-leitura. Use "Editar como nova versão" para forkar o conteúdo num
            rascunho editável.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-2">
          {isLoading && <p className="py-6 text-sm text-muted-foreground">Carregando…</p>}
          {isError && (
            <div className="space-y-2 py-6">
              <p className="text-sm text-destructive">Falha ao carregar conteúdo da versão.</p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}
          {data && <PromptEditor value={editorValue} onChange={noop} disabled />}
        </div>

        <DialogFooter className="mt-4">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
          {data && onForkAsNewVersion && (
            <Button
              type="button"
              onClick={() => {
                onForkAsNewVersion(toEditorValue(data));
                onOpenChange(false);
              }}
            >
              Editar como nova versão
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ViewVersionDialog;
